import React, { useState } from 'react';

function NewPortfolioPage({
  darkMode,
  isEditing,
  githubUrl,
  setGithubUrl,
  fetchRepoData,
  newPortfolio,
  setNewPortfolio,
  handlePortfolioSubmit,
  setActivePage,
  setIsEditing,
}) {
  const [previewImage, setPreviewImage] = useState(newPortfolio.imageUrl || '/api/placeholder/600/400');

  // Handle file input change for image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a URL for the file for preview
      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl);
      
      // Update the portfolio with the file (you'll need to handle file upload on submit)
      setNewPortfolio({ ...newPortfolio, imageFile: file, imageUrl: 'uploaded-image' });
    }
  };

  return (
    <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
      <h2 className="text-3xl font-bold mb-6">
        {isEditing ? 'Edit Portfolio Project' : 'Add New Portfolio Project'}
      </h2>
      {/* GitHub Repo URL input */}
      <div className="mb-4">
        <label className="block mb-2 font-medium">GitHub Repository URL</label>
        <div className="flex">
          <input
            type="text"
            value={githubUrl}
            onChange={(e) => setGithubUrl(e.target.value)}
            className={`w-full p-2 rounded-l-md ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'} border`}
            placeholder="https://github.com/username/repo"
          />
          <button
            type="button"
            onClick={fetchRepoData}
            className={`px-4 py-2 rounded-r-md ${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white`}
          >
            Fetch Data
          </button>
        </div>
      </div>
      <form onSubmit={handlePortfolioSubmit}>
        <div className="mb-4">
          <label className="block mb-2 font-medium">Project Title</label>
          <input
            type="text"
            value={newPortfolio.title}
            onChange={(e) => setNewPortfolio({ ...newPortfolio, title: e.target.value })}
            className={`w-full p-2 rounded-md ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'} border`}
            placeholder="Enter project title"
            required
          />
        </div>
        <div className="mb-4">
          <label className="block mb-2 font-medium">Description</label>
          <textarea
            value={newPortfolio.description}
            onChange={(e) => setNewPortfolio({ ...newPortfolio, description: e.target.value })}
            className={`w-full p-2 rounded-md ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'} border`}
            placeholder="Describe your project..."
            rows="5"
            required
          ></textarea>
        </div>
        
        {/* Image Upload Section */}
        <div className="mb-4">
          <label className="block mb-2 font-medium">Project Image</label>
          <div className="flex flex-col space-y-4">
            {/* Image preview */}
            <div className="w-full">
              <img 
                src={previewImage} 
                alt="Project preview" 
                className="max-h-64 rounded-md object-cover border"
              />
            </div>
            
            {/* File input for image upload */}
            <div className="flex items-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className={`w-full p-2 rounded-md ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'} border`}
                id="project-image"
              />
            </div>
            
            {/* Image URL option (as alternative to upload) */}
            <div>
              <label className="block mb-2 text-sm font-medium">Or enter image URL</label>
              <input
                type="text"
                value={newPortfolio.imageUrl === 'uploaded-image' ? '' : newPortfolio.imageUrl}
                onChange={(e) => {
                  setNewPortfolio({ ...newPortfolio, imageUrl: e.target.value, imageFile: null });
                  setPreviewImage(e.target.value || '/api/placeholder/600/400');
                }}
                className={`w-full p-2 rounded-md ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'} border`}
                placeholder="https://your-image-url.com/image.jpg"
              />
              <p className="text-sm text-gray-500 mt-1">
                You can upload an image or provide a URL. If neither is provided, a placeholder will be used.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block mb-2 font-medium">Tags (comma separated)</label>
          <input
            type="text"
            value={newPortfolio.tags}
            onChange={(e) => setNewPortfolio({ ...newPortfolio, tags: e.target.value })}
            className={`w-full p-2 rounded-md ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'} border`}
            placeholder="React, Web Development, Design"
            required
          />
        </div>
        <div className="mb-4">
          <label className="block mb-2 font-medium">Project Link</label>
          <input
            type="text"
            value={newPortfolio.link}
            onChange={(e) => setNewPortfolio({ ...newPortfolio, link: e.target.value })}
            className={`w-full p-2 rounded-md ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'} border`}
            placeholder="https://your-project-url.com"
          />
        </div>
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => {
              setNewPortfolio({
                title: '',
                description: '',
                imageUrl: '/api/placeholder/600/400',
                imageFile: null,
                tags: '',
                link: '#',
              });
              setIsEditing(false);
              setActivePage('portfolio');
            }}
            className={`px-4 py-2 rounded-md ${darkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-300 hover:bg-gray-400'}`}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`px-4 py-2 rounded-md ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
          >
            {isEditing ? 'Update Project' : 'Add Project'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default NewPortfolioPage;